import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api';
import { Gift, Users, FlaskConical, Star, DollarSign, FileText } from 'lucide-react';
import './Profile.css';

function Profile() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [stats, setStats] = useState(null);
  const [veraBalance, setVeraBalance] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [telegramUser, setTelegramUser] = useState(null);
  const [showExtendModal, setShowExtendModal] = useState(false);
  const [extending, setExtending] = useState(false);
  const [showTestsModal, setShowTestsModal] = useState(false);
  const [tests, setTests] = useState([]);
  const [loadingTests, setLoadingTests] = useState(false);

  useEffect(() => {
    // Get Telegram user data for avatar
    if (window.Telegram?.WebApp) {
      const tg = window.Telegram.WebApp;
      const tgUser = tg.initDataUnsafe?.user;
      if (tgUser) {
        setTelegramUser(tgUser);
      }
    }

    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [userRes, statsRes, balanceRes, transactionsRes] = await Promise.all([
        api.get('/user/me'),
        api.get('/user/stats'),
        api.get('/vera/balance'),
        api.get('/vera/transactions', { params: { limit: 5 } })
      ]);

      setUser(userRes.data);
      setStats(statsRes.data);
      setVeraBalance(balanceRes.data);
      setTransactions(transactionsRes.data);
    } catch (error) {
      console.error('Failed to load profile data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadTests = async () => {
    setLoadingTests(true);
    try {
      const response = await api.get('/feedback/my-tests', { params: { limit: 50 } });
      setTests(response.data.tests || []);
    } catch (error) {
      console.error('Failed to load tests:', error);
    } finally {
      setLoadingTests(false);
    }
  };

  const handleShowTests = () => {
    setShowTestsModal(true);
    if (tests.length === 0) {
      loadTests();
    }
  };

  const handleTelegramClick = () => {
    if (window.Telegram?.WebApp) {
      window.Telegram.WebApp.HapticFeedback?.impactOccurred('light');
    }
    window.Telegram?.WebApp?.openTelegramLink('https://t.me/+wBXI4N49pwdhNDY1');
  };

  const handleExtendSubscription = async () => {
    if (extending) return;

    const veraPoints = veraBalance?.vera_points || 0;
    if (veraPoints < 100) {
      window.Telegram?.WebApp?.showAlert('Недостаточно VERA очков. Нужно минимум 100 очков для продления на 1 день.');
      return;
    }

    setExtending(true);
    try {
      await api.post('/vera/extend-subscription', { days: 1 });

      window.Telegram?.WebApp?.showAlert('✅ Подписка успешно продлена на 1 день!');
      setShowExtendModal(false);

      // Reload data
      await loadData();
    } catch (error) {
      console.error('Failed to extend subscription:', error);
      window.Telegram?.WebApp?.showAlert('Ошибка при продлении подписки');
    } finally {
      setExtending(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getTransactionIcon = (type) => {
    const iconComponents = {
      'registration': Gift,
      'invite_bonus': Users,
      'test_complete': FlaskConical,
      'admin_bonus': Star,
      'spent': DollarSign
    };
    const IconComponent = iconComponents[type] || FileText;
    return <IconComponent size={24} strokeWidth={2} />;
  };

  const getTransactionDescription = (tx) => {
    const descriptions = {
      'registration': 'Бонус за регистрацию',
      'invite_bonus': 'Бонус за приглашение',
      'test_complete': 'Завершен тест',
      'admin_bonus': 'Бонус от администратора',
      'spent': 'Потрачено'
    };
    return tx.description || descriptions[tx.transaction_type] || tx.transaction_type;
  };

  if (loading) {
    return (
      <div className="profile-loading">
        <div className="spinner"></div>
      </div>
    );
  }

  const userName = user?.first_name || telegramUser?.first_name || 'User';
  const userLastName = user?.last_name || telegramUser?.last_name || '';
  const username = user?.username || telegramUser?.username || '';
  const userPhoto = telegramUser?.photo_url;

  return (
    <div className="profile-page">
      {/* Header with Points */}
      <div className="profile-header">
        <div className="points-display">
          <span className="points-icon">⭐</span>
          <span className="points-value">{veraBalance?.total_points || 0}</span>
          <span className="points-label">очков</span>
        </div>
        <div className="header-right">
          {telegramUser && (
            <div className="user-greeting">
              {telegramUser.first_name} 👋
            </div>
          )}
          <button className="telegram-chat-btn" onClick={handleTelegramClick} title="Присоединиться к чату">
            <svg className="telegram-icon" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm5.562 8.161c-.18.717-1.558 7.315-2.2 9.712-.271.996-.806 1.33-1.321 1.362-.563.035-1.175-.232-1.823-.58-1.011-.543-1.582-.88-2.563-1.41-1.133-.612-.399-1.013.247-1.601.169-.154 3.107-2.85 3.165-3.094.007-.031.014-.145-.054-.206s-.164-.04-.234-.024c-.1.024-1.688 1.073-4.764 3.149-.451.311-.86.462-1.226.454-.404-.009-1.18-.228-1.758-.416-.709-.23-1.272-.352-1.223-.743.025-.203.31-.41.855-.62 3.35-1.458 5.583-2.419 6.7-2.882 3.19-1.326 3.852-1.556 4.285-1.563.095-.002.307.022.445.134.116.094.148.221.163.31.015.088.034.29.019.447z"/>
            </svg>
          </button>
        </div>
      </div>

      {/* Profile Header Card */}
      <div className="profile-header-card">
        <div className="profile-avatar">
          {userPhoto ? (
            <img src={userPhoto} alt={userName} className="profile-avatar-img" />
          ) : (
            <span className="profile-avatar-letter">{userName[0]?.toUpperCase() || 'U'}</span>
          )}
        </div>
        <div className="profile-info">
          <h1 className="profile-name">{userName} {userLastName}</h1>
          {username && <p className="profile-username">@{username}</p>}
        </div>
      </div>

      {/* VERA Points Widget - Elegant Purple/Blue */}
      <div className="vera-points-widget">
        <div className="vera-widget-header">
          <span className="vera-widget-title">VERA</span>
          <span className="vera-crown-icon">👑</span>
        </div>
        <div className="vera-widget-main">
          <div className="vera-total">
            <span className="vera-total-number">{veraBalance?.vera_points || 0}</span>
          </div>
          <div className="vera-breakdown">
            <div className="vera-breakdown-item">
              <span className="vera-breakdown-value earned">+{veraBalance?.total_earned || 0}</span>
              <span className="vera-breakdown-label">ЗАРАБОТАНО</span>
            </div>
            <div className="vera-breakdown-item">
              <span className="vera-breakdown-value spent">-{veraBalance?.total_spent || 0}</span>
              <span className="vera-breakdown-label">ПОТРАЧЕНО</span>
            </div>
          </div>
        </div>
      </div>

      {/* Statistics Grid (2x2) */}
      <div className="stats-grid-2x2">
        <div
          className="stat-card-modern clickable"
          onClick={() => navigate('/tests')}
        >
          <div className="stat-card-icon">🧪</div>
          <div className="stat-card-content">
            <div className="stat-card-value">{stats?.total_tests || 0}</div>
            <div className="stat-card-label">ТЕСТОВ</div>
          </div>
        </div>
        <div className="stat-card-modern">
          <div className="stat-card-icon success">✅</div>
          <div className="stat-card-content">
            <div className="stat-card-value">{stats?.success_rate || 0}%</div>
            <div className="stat-card-label">УСПЕХ</div>
          </div>
        </div>
        <div
          className="stat-card-modern clickable"
          onClick={() => navigate('/invites')}
        >
          <div className="stat-card-icon">👥</div>
          <div className="stat-card-content">
            <div className="stat-card-value">{stats?.invites_used || 0}/{stats?.invite_limit || 10}</div>
            <div className="stat-card-label">ПРИГЛАШЕНИЙ</div>
          </div>
        </div>
        <div
          className="stat-card-modern clickable"
          onClick={() => setShowExtendModal(true)}
        >
          <div className="stat-card-icon">⏰</div>
          <div className="stat-card-content">
            <div className="stat-card-value">{stats?.days_left || 7}</div>
            <div className="stat-card-label">ДНЕЙ ОСТАЛОСЬ</div>
            <div className="stat-card-hint">Нажмите для продления</div>
          </div>
        </div>
      </div>

      {/* Extend Subscription Modal */}
      {showExtendModal && (
        <div className="modal-overlay" onClick={() => !extending && setShowExtendModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Продлить подписку</h3>
            <p className="modal-description">
              Вы можете продлить подписку на 1 день за 100 VERA очков
            </p>
            <div className="modal-stats">
              <div className="modal-stat">
                <span className="modal-stat-label">У вас сейчас:</span>
                <span className="modal-stat-value">{veraBalance?.vera_points || 0} VERA</span>
              </div>
              <div className="modal-stat">
                <span className="modal-stat-label">Стоимость:</span>
                <span className="modal-stat-value">100 VERA</span>
              </div>
              <div className="modal-stat">
                <span className="modal-stat-label">После продления:</span>
                <span className="modal-stat-value">{(veraBalance?.vera_points || 0) - 100} VERA</span>
              </div>
            </div>
            <div className="modal-actions">
              <button
                className="modal-btn cancel"
                onClick={() => setShowExtendModal(false)}
                disabled={extending}
              >
                Отмена
              </button>
              <button
                className="modal-btn confirm"
                onClick={handleExtendSubscription}
                disabled={extending || (veraBalance?.vera_points || 0) < 100}
              >
                {extending ? 'Продление...' : 'Продлить'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Settings Button */}
      <div className="profile-settings-section">
        <button
          className="settings-btn-profile"
          onClick={() => navigate('/settings')}
        >
          <span className="settings-icon">⚙️</span>
          <span className="settings-text">Настройки</span>
          <span className="settings-arrow">→</span>
        </button>
      </div>

      {/* Recent Transactions */}
      <div className="transactions-section">
        <h3 className="section-title">Последние транзакции</h3>
        {transactions.length === 0 ? (
          <div className="no-transactions-card">
            <span className="no-transactions-icon">📊</span>
            <p>Пока нет транзакций</p>
          </div>
        ) : (
          <div className="transactions-list-modern">
            {transactions.map((tx) => (
              <div key={tx.id} className="transaction-card-modern">
                <div className="transaction-left">
                  <div className="transaction-icon-modern">
                    {getTransactionIcon(tx.transaction_type)}
                  </div>
                  <div className="transaction-details">
                    <div className="transaction-description">
                      {getTransactionDescription(tx)}
                    </div>
                    <div className="transaction-timestamp">
                      {formatDate(tx.created_at)}
                    </div>
                  </div>
                </div>
                <div className={`transaction-amount-modern ${tx.amount > 0 ? 'positive' : 'negative'}`}>
                  {tx.amount > 0 ? '+' : ''}{tx.amount}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default Profile;
